import { MouseEvent, useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { AddIcon, colors } from "@dotnet/telematics-design-system";

import { useDialog } from "components/Actions/ActionBar/hooks/useDialog";
import CreateOfflineLicense from "components/Actions/dialogs/Licenses/OfflineLicenses/CreateOfflineLicense";
import { LICENSE_ACTIONS } from "components/Actions/types/enums";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import TableContext from "context/table/tableContext";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { useOverflow } from "shared/hooks/useOverflow";

import { hasLicenseExpired } from "../functions";
import { HeaderProps } from "../types";

const Header = ({ isDetailsTabSelected }: HeaderProps) => {
    const { gray900, textPlaceholder } = colors;

    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();
    const { ref, isOverflowing } = useOverflow();
    const { renderFormattedNumber } = useFormattedNumber();
    const { isOpenDialog, closeDialog, openDialog } = useDialog();

    const { expires_at, imei_restricted, name, offline_licenses } = detailsData;

    const handleOpenDialog = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        openDialog(LICENSE_ACTIONS.CreateOfflineLicense);
    };

    const handleCloseDialog = () =>
        closeDialog(LICENSE_ACTIONS.CreateOfflineLicense);

    const isDesktopLicenseUnrestricted = !imei_restricted;
    const hasDesktopLicenseExpired = hasLicenseExpired(expires_at);

    const isCreateOfflineLicenseDisabled =
        isDesktopLicenseUnrestricted || hasDesktopLicenseExpired;

    const getCreateOfflineLicenseTooltipTitle = () => {
        if (isDesktopLicenseUnrestricted) {
            return t("Button##cannot create offline licenses");
        }

        return hasDesktopLicenseExpired
            ? t("General##license has expired")
            : "";
    };

    return (
        <>
            <div
                css={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    columnGap: "32px",
                    marginBottom: "16px",

                    "@media (max-width: 839px)": {
                        display: "block",
                        margin: "0 60px 24px 0"
                    }
                })}
            >
                <div
                    css={css({
                        overflowX: "hidden"
                    })}
                >
                    <div
                        css={css({
                            color: gray900,
                            fontSize: "24px",
                            fontWeight: "500",
                            lineHeight: "32px",
                            marginBottom: "4px"
                        })}
                    >
                        <TooltipGeneral
                            title={name}
                            placement={TOOLTIP_PLACEMENT.Top}
                            disableHoverListener={!isOverflowing}
                        >
                            <span
                                ref={ref}
                                css={css({
                                    display: "block",
                                    whiteSpace: "nowrap",
                                    overflowX: "hidden",
                                    textOverflow: "ellipsis"
                                })}
                            >
                                {name}
                            </span>
                        </TooltipGeneral>
                    </div>

                    <div
                        css={css({
                            fontSize: "14px",
                            fontWeight: "500",
                            lineHeight: "20px",
                            letterSpacing: "0.1px",

                            "span:first-of-type": {
                                color: textPlaceholder,
                                paddingRight: "5px"
                            },

                            "span:last-of-type": {
                                color: gray900
                            }
                        })}
                    >
                        <span>{t("General##offline licenses")}:</span>

                        <span>
                            {renderFormattedNumber(offline_licenses.length)}
                        </span>
                    </div>
                </div>

                {!isDetailsTabSelected && (
                    <TooltipGeneral
                        title={getCreateOfflineLicenseTooltipTitle()}
                        placement={TOOLTIP_PLACEMENT.Top}
                        disableTouchListener
                    >
                        <span
                            css={css({
                                marginRight: "60px",

                                "@media (max-width: 839px)": {
                                    marginRight: "0",
                                    marginTop: "16px"
                                }
                            })}
                        >
                            <Button
                                color={BUTTON_COLORS.Secondary}
                                size={BUTTON_SIZES.Small}
                                variant={BUTTON_VARIANTS.IconLeft}
                                icon={<AddIcon />}
                                onClick={handleOpenDialog}
                                disabled={isCreateOfflineLicenseDisabled}
                            >
                                {t("Button##create")}
                            </Button>
                        </span>
                    </TooltipGeneral>
                )}
            </div>

            <CreateOfflineLicense
                isOpen={isOpenDialog[LICENSE_ACTIONS.CreateOfflineLicense]}
                close={handleCloseDialog}
            />
        </>
    );
};

export default Header;
